import { Globe } from "lucide-react";
import { cn } from "@/utils/helpers";
import PlatformGlyph, { hasGlyph } from "./PlatformGlyph";

/**
 * The platform mark a plate wears in the corner of its media, on EVERY wall.
 *
 * Trends and References both say "this came from TikTok" and they had drifted
 * into saying it two different ways: Trends drew a 24px logo box in the top
 * right, References drew a bordered uppercase word in the top left. Two walls
 * sitting next to each other in the nav cannot disagree about what a source
 * looks like, so this is the one component and neither wall draws its own.
 *
 * A LOGO, never a word. The word is the accessible name and lives in the
 * sr-only span, which is what keeps the frame one fixed 24px square whatever
 * platform it carries — a text chip is as wide as its longest label and
 * INSTAGRAM beside WEB is two different objects.
 *
 * `web` has no brand logo, so it takes a globe in the same frame rather than
 * falling back to a word and breaking the shape. Anything unrecognised takes
 * the same glyph: the badge says "there is a source and it is not one of the
 * platforms we draw", which is true, where a made-up mark would not be.
 */

export type BadgePlatform = "tiktok" | "instagram" | "youtube" | "web";

/**
 * The glyphs moved to PlatformGlyph, which is the one place a mark is drawn.
 *
 * This file's own rule was "this is the one component and neither wall draws
 * its own", and it had quietly become the third place: Trends drew here,
 * References drew here, and the calendar drew uppercase text. Worse, the marks
 * here were lucide's line icons for Instagram and YouTube and the real glyph
 * for TikTok — one platform official, two approximated, in the same row.
 *
 * NOTE: this makes Instagram and YouTube on Trends and References the official
 * filled marks rather than lucide outlines. Same size, same frame, heavier ink.
 */
function Badge({ platform, className }: { platform: string; className?: string }) {
  /* `web` has no brand logo, and neither does anything unrecognised. Both take
     the globe rather than falling back to a word and breaking the fixed square. */
  if (!hasGlyph(platform)) {
    return <Globe className={className} strokeWidth={2.25} aria-hidden="true" />;
  }
  return <PlatformGlyph platform={platform.toUpperCase()} className={className} />;
}

export default function PlatformBadge({
  platform,
  label,
  className,
  surface = "page",
}: {
  /** The wire value. Unrecognised values get the globe, never a word. */
  platform: string;
  /** The accessible name — "TikTok", "Web". Printed for a screen reader only. */
  label: string;
  className?: string;
  /** What the badge stands on. "page" is the opaque bordered square for a
   *  caption on the page ground. "media" is the media-glass chip for a mark
   *  that sits ON a video: white ink on dark frost at the same values in both
   *  themes, because the frame behind it can be any colour. */
  surface?: "page" | "media";
}) {
  if (surface === "media") {
    return (
      <span className={cn("co-media-chip", className)}>
        <Badge platform={platform} className="size-[13px]" />
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex size-6 items-center justify-center border border-co-line bg-co-surface text-co-muted",
        className,
      )}
    >
      <Badge platform={platform} className="size-3.5" />
      <span className="sr-only">{label}</span>
    </span>
  );
}
